import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  LogIn, 
  AlertCircle,
  BarChart3,
  TrendingUp,
  Users,
  Target
} from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    
    try {
      const result = await authService.login(formData.email, formData.password);
      console.log('Login successful:', result);
      
      // Data is already stored in localStorage by authService.login
      
      // Navigate based on role
      if (result.user.role === 'administrator') {
        navigate('/admin/dashboard');
      } else {
        navigate('/user/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        
        {/* Left Side - Branding & Features */}
        <div className="hidden lg:block">
          <div className="space-y-8">
            {/* Logo & Title */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#508742] to-[#3A6B30] rounded-2xl flex items-center justify-center shadow-lg shadow-[#508742]/20">
                  <img src="src/Moahi_white.png"
                       alt="Logo"
                       className="w-40 h-40 object-contain"/>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white">Moahi Sustainable Solutions</h1>
                  <p className="text-[#508742] font-semibold">Sales Tracker</p>
                </div>
              </div>
              <p className="text-slate-400 text-lg">
                Professional sales pipeline management system
              </p>
            </div>

            {/* Features */}
            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Real-time Analytics</h3>
                  <p className="text-slate-400 text-sm">
                    Track your sales pipeline with live dashboards and insights
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Team Collaboration</h3>
                  <p className="text-slate-400 text-sm">
                    Manage leads, consultants, and performance across your team
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Lead Management</h3>
                  <p className="text-slate-400 text-sm">
                    Comprehensive lead tracking from first contact to conversion
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-slate-800">
              <div>
                <p className="text-3xl font-bold text-white mb-1">172</p>
                <p className="text-slate-500 text-sm">Active Leads</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-1">R660K</p>
                <p className="text-slate-500 text-sm">Pipeline Value</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-1">6</p>
                <p className="text-slate-500 text-sm">Consultants</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 mx-auto mb-4">
              <BarChart3 className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1">Moahi Sales Tracker</h1>
            <p className="text-slate-400">Sign in to your account</p>
          </div>

          {/* Login Card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
            <div className="hidden lg:block mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Welcome back</h2>
              <p className="text-slate-400">Sign in to access your dashboard</p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@moahisolutions.com"
                    autoComplete="email"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="w-full pl-11 pr-12 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded bg-slate-800 border-slate-700 text-[#508742] focus:ring-2 focus:ring-amber-500 cursor-pointer"
                  />
                  <span className="text-sm text-slate-400">Remember me</span>
                </label>
                <button
                  type="button"
                  className="text-sm text-[#508742] hover:text-[#3A6B30] font-medium transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-[#508742] to-[#3A6B30] hover:from-[#5f9650] hover:to-[#2f5a27] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#508742]/20 hover:shadow-[#508742]/40"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Need help?{' '}
                <button className="text-[#508742] hover:text-[#3A6B30] font-medium transition-colors">
                  Contact Support
                </button>
              </p>
            </div>
          </div>

          {/* Copyright */}
          <p className="text-center text-slate-600 text-sm mt-6">
            © 2025 Moahi Sustainable Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}